import { useState } from "react";
import { Bot, Loader2, Check, X, Play, Zap, Clock, AlertTriangle, CheckCircle2, Sunrise } from "lucide-react";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import {
  useRules,
  useProposals,
  useUpdateRule,
  useApproveProposal,
  useRejectProposal,
  useRunNow,
  type Automatizacion,
  type Ejecucion,
} from "../api/automation";

export function AutomationPage() {
  const { data: rules, isLoading: loadingRules } = useRules();
  const { data: proposals, isLoading: loadingProps } = useProposals();
  const run = useRunNow();
  const [runMsg, setRunMsg] = useState<string | null>(null);

  useGlobalNavigationTarget("automations", (target) => {
    if (!target.anchor) return;

    window.setTimeout(() => {
      document.getElementById(target.anchor ?? "")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  });

  const activas = rules?.filter((r) => r.activo).length ?? 0;

  async function evaluar() {
    setRunMsg(null);
    if (activas === 0) {
      setRunMsg("No hay ninguna regla activa. Activa al menos una con su interruptor.");
      setTimeout(() => setRunMsg(null), 6000);
      return;
    }
    const r = (await run.mutateAsync()) as { propuestas: number; ejecutadas: number };
    setRunMsg(`Evaluación completada: ${r.propuestas} propuesta(s) nueva(s), ${r.ejecutadas} ejecutada(s) automáticamente.`);
    setTimeout(() => setRunMsg(null), 6000);
  }

  return (
    <div className="p-8 h-full flex flex-col gap-6 min-h-0 overflow-auto">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <Bot size={28} className="text-brand-700" /> Automatizaciones
          </h1>
          <p className="text-slate-500 mt-1">
            Tu asistente proactivo: detecta y propone acciones. Tú apruebas, él ejecuta.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={evaluar}
            disabled={run.isPending}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 hover:bg-slate-50 px-4 py-2.5 text-sm font-bold text-slate-700 disabled:opacity-50"
            title="Evaluar las reglas ahora"
          >
            {run.isPending ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Evaluar ahora
          </button>
          {runMsg && <span className="text-xs font-semibold text-slate-500 max-w-[260px] text-right">{runMsg}</span>}
        </div>
      </header>

      {/* Briefing del día */}
      {proposals?.briefing && (
        <section id="automations-briefing" className="scroll-mt-6 rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-5 shadow-soft">
          <div className="flex items-center gap-2 mb-2">
            <Sunrise size={18} className="text-brand-700" />
            <h2 className="font-black text-slate-900">{proposals.briefing.titulo}</h2>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{proposals.briefing.resumen}</p>
        </section>
      )}

      {/* Bandeja de propuestas pendientes */}
      <section id="automations-proposals" className="scroll-mt-6">
        <h2 className="text-lg font-black text-slate-900 mb-3 flex items-center gap-2">
          <Zap size={18} className="text-amber-500" /> Propuestas pendientes
          {proposals?.pendientes.length ? (
            <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs">{proposals.pendientes.length}</span>
          ) : null}
        </h2>
        {loadingProps ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
        ) : !proposals?.pendientes.length ? (
          <p className="text-sm text-slate-400 rounded-2xl border border-dashed border-slate-200 p-6 text-center">
            No hay nada pendiente de aprobar. El asistente te avisará cuando detecte algo.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {proposals.pendientes.map((p) => <ProposalCard key={p.id} p={p} />)}
          </div>
        )}
      </section>

      {/* Reglas */}
      <section id="automations-rules" className="scroll-mt-6">
        <h2 className="text-lg font-black text-slate-900 mb-3">Reglas</h2>
        {loadingRules ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {rules?.map((r) => <RuleCard key={r.id} r={r} />)}
          </div>
        )}
      </section>

      {/* Historial */}
      {proposals?.historial.length ? (
        <section id="automations-history" className="scroll-mt-6">
          <h2 className="text-lg font-black text-slate-900 mb-3">Historial reciente</h2>
          <div className="rounded-2xl border border-slate-200/70 bg-white divide-y divide-slate-100">
            {proposals.historial.map((h) => <HistoryRow key={h.id} h={h} />)}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ProposalCard({ p }: { p: Ejecucion }) {
  const approve = useApproveProposal();
  const reject = useRejectProposal();
  const busy = approve.isPending || reject.isPending;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 shadow-soft">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl grid place-items-center bg-amber-100 text-amber-700 shrink-0"><Zap size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{p.automatizacion?.nombre}</div>
          <div className="font-black text-slate-900">{p.titulo}</div>
          {p.resumen && <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{p.resumen}</p>}
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={() => { if (confirm("¿Rechazar esta propuesta?")) reject.mutate(p.id); }}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 hover:bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:opacity-50"
        >
          <X size={15} /> Rechazar
        </button>
        <button
          onClick={() => { if (confirm("Aprobar ejecutará la acción de verdad (p. ej. enviar emails). ¿Continuar?")) approve.mutate(p.id); }}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
        >
          {approve.isPending ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Aprobar y ejecutar
        </button>
      </div>
    </div>
  );
}

function RuleCard({ r }: { r: Automatizacion }) {
  const update = useUpdateRule();
  const dia = Number((r.config as Record<string, unknown>)?.dia_del_mes ?? 0);

  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-black text-slate-900">{r.nombre}</div>
          <p className="text-sm text-slate-500 mt-0.5">{r.descripcion}</p>
        </div>
        <label className="inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={r.activo}
            onChange={(e) => update.mutate({ id: r.id, payload: { activo: e.target.checked } })}
          />
          <div className="relative w-11 h-6 bg-slate-200 peer-checked:bg-brand-600 rounded-full transition after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition peer-checked:after:translate-x-5" />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <label className="text-sm text-slate-600 flex items-center gap-2">
          Modo:
          <select
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm font-semibold"
            value={r.modo}
            onChange={(e) => update.mutate({ id: r.id, payload: { modo: e.target.value as "aprobacion" | "auto" } })}
          >
            <option value="aprobacion">Con aprobación</option>
            <option value="auto">Automático</option>
          </select>
        </label>
        {r.tipo === "nominas_fin_mes" && (
          <label className="text-sm text-slate-600 flex items-center gap-2">
            Día del mes:
            <input
              type="number" min={1} max={31}
              className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-sm"
              defaultValue={dia || 25}
              onBlur={(e) => update.mutate({ id: r.id, payload: { config: { ...(r.config ?? {}), dia_del_mes: Number(e.target.value) } } })}
            />
          </label>
        )}
        {(r.tipo === "recordatorio_pre_vencimiento" || r.tipo === "plazos_fiscales" || r.tipo === "contratos_caducan") && (
          <label className="text-sm text-slate-600 flex items-center gap-2">
            Días antes:
            <input
              type="number" min={1} max={90}
              className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-sm"
              defaultValue={Number((r.config as Record<string, unknown>)?.dias_antes ?? 7)}
              onBlur={(e) => update.mutate({ id: r.id, payload: { config: { ...(r.config ?? {}), dias_antes: Number(e.target.value) } } })}
            />
          </label>
        )}
        {r.tipo === "oportunidad_estancada" && (
          <label className="text-sm text-slate-600 flex items-center gap-2">
            Días sin movimiento:
            <input
              type="number" min={1} max={120}
              className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-sm"
              defaultValue={Number((r.config as Record<string, unknown>)?.dias_sin_movimiento ?? 14)}
              onBlur={(e) => update.mutate({ id: r.id, payload: { config: { ...(r.config ?? {}), dias_sin_movimiento: Number(e.target.value) } } })}
            />
          </label>
        )}
        {r.tipo === "lead_caliente" && (
          <label className="text-sm text-slate-600 flex items-center gap-2">
            Umbral de score:
            <input
              type="number" min={1} max={100}
              className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-sm"
              defaultValue={Number((r.config as Record<string, unknown>)?.umbral ?? 70)}
              onBlur={(e) => update.mutate({ id: r.id, payload: { config: { ...(r.config ?? {}), umbral: Number(e.target.value) } } })}
            />
          </label>
        )}
      </div>
      {r.modo === "auto" && (
        <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
          <AlertTriangle size={13} /> En automático ejecuta sin pedirte aprobación.
        </p>
      )}
      {r.ultima_ejecucion && (
        <p className="mt-2 text-xs text-slate-400 flex items-center gap-1"><Clock size={12} /> Última evaluación: {new Date(r.ultima_ejecucion).toLocaleString("es-ES")}</p>
      )}
    </div>
  );
}

function HistoryRow({ h }: { h: Ejecucion }) {
  const ok = h.estado === "ejecutada";
  const err = h.estado === "error";
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      {ok ? <CheckCircle2 size={16} className="text-emerald-600" /> : err ? <AlertTriangle size={16} className="text-rose-500" /> : <X size={16} className="text-slate-400" />}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-800 truncate">{h.titulo}</div>
        <div className="text-xs text-slate-400">{h.automatizacion?.nombre} · {new Date(h.creado_en).toLocaleString("es-ES")}</div>
      </div>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ok ? "bg-emerald-50 text-emerald-700" : err ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
        {h.estado}
      </span>
    </div>
  );
}
